/* eslint-disable camelcase */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { ReactElement } from 'react';
import { moment } from '@hotjar/webapp/lib/moment';
import { BasePopover, Icon, Text, tokens } from '@hotjar/hotsauce';
import type { PriceSummary } from '@hotjar/webapp/billing/subscription';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useOwnedAccount } from '@hotjar/account';
import { usePrettifyPrice } from '@hotjar/webapp/hooks/usePrettifyPrice';
import * as SharedStyles from './PlanChangeConfirmation.styles';

const S = {
  ...SharedStyles,
  Container: styled.div`
    background-color: ${tokens.color.ui_background};
    border-radius: ${tokens.radius.m};
    border: 1px solid ${tokens.color.ui_border_light};
  `,
  Item: styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${tokens.spacer.x2};
    padding: ${tokens.spacer.x2};
    &:not(:last-of-type) {
      border-bottom: 1px solid ${tokens.color.ui_border_light};
    }
  `,
  ItemInfo: styled.div`
    flex: 1;
  `,
  ItemTitle: styled.h3`
    ${tokens.text('subtitle2')};
    color: ${tokens.color.text_default};
  `,
  ItemInfoText: styled.p`
    ${tokens.text('caption')};
    color: ${tokens.color.text_light};
    margin: 0;
    padding-top: ${tokens.spacer.x0};
  `,
  ItemCosts: styled.div`
    text-align: right;
  `,
  PopoverItem: styled.div`
    display: flex;
    justify-content: space-between;

    :not(:last-of-type) {
      margin-bottom: ${tokens.spacer.x2};
      color: ${tokens.color.text_light};
      ${tokens.text('body2')};
    }

    :last-of-type {
      padding-top: ${tokens.spacer.x2};
      border-top: 1px solid ${tokens.color.ui_border_light};
      ${tokens.text('subtitle2')};
    }
  `,
  Icon: styled(Icon)`
    cursor: pointer;
  `,
};

type CostBreakdown = Pick<PriceSummary, 'tax' | 'total' | 'totalWithTax'>;

interface PlansCostItemProps {
  costs: CostBreakdown;
  onPopoverClicked(position: string): void;
  infoTitle?: string;
  infoDescription?: string;
  popoverTitle?: string;
  billingDate?: string;
}

const PlanCostItem = ({
  costs,
  onPopoverClicked,
  infoTitle,
  infoDescription,
  popoverTitle,
  billingDate,
}: PlansCostItemProps): ReactElement => {
  const { t: tSummary } = useTranslationWithPrefix(`billing:summary-of-changes.cost`);
  const { prettifyPrice } = usePrettifyPrice();

  return (
    <S.Item>
      <S.ItemInfo>
        <S.ItemTitle>{infoTitle}</S.ItemTitle>
        {billingDate && <S.ItemInfoText>{billingDate}.</S.ItemInfoText>}
        <S.ItemInfoText>{infoDescription}</S.ItemInfoText>
      </S.ItemInfo>
      <S.ItemCosts>
        <S.ItemTitle>{prettifyPrice(costs.totalWithTax)}</S.ItemTitle>
        <Text
          as="p"
          textType="caption"
          textColor="text_light"
          css={css`
            line-height: ${tokens.spacer.x3};
            margin: 0;
            display: flex;
            align-items: center;
            gap: ${tokens.spacer.x0};
          `}
        >
          {tSummary('tax-included')}{' '}
          <BasePopover
            title={popoverTitle}
            icon="info"
            onShow={() => onPopoverClicked(infoTitle ?? '')}
            content={
              <>
                <S.PopoverItem>
                  <span>{tSummary('subtotal')}</span>
                  <span>{prettifyPrice(costs.total)}</span>
                </S.PopoverItem>

                <S.PopoverItem>
                  <span>
                    {tSummary('tax')} ({costs.tax.rate.toFixed()}%)
                  </span>
                  <span>{prettifyPrice(costs.tax.amount)}</span>
                </S.PopoverItem>

                <S.PopoverItem>
                  <span>{tSummary('total')}</span>
                  <span>{prettifyPrice(costs.totalWithTax)}</span>
                </S.PopoverItem>
              </>
            }
          >
            <S.Icon icon="info" />
          </BasePopover>
        </Text>
      </S.ItemCosts>
    </S.Item>
  );
};

export interface PlansCostProps {
  payNowPrice: CostBreakdown | undefined;
  payLaterPrice: CostBreakdown | undefined;
  onPopoverClicked(position: string): void;
}

export const PlansCostNewSummary = ({
  payNowPrice,
  payLaterPrice,
  onPopoverClicked,
}: PlansCostProps): ReactElement => {
  const { data: account } = useOwnedAccount();
  const { t: tConfirmation } = useTranslationWithPrefix(`billing:plan-change-confirmation.cost`);
  const { t: tSummary } = useTranslationWithPrefix(`billing:summary-of-changes.cost`);

  const nextBillingDate = moment.unix(account?.nextBillingDate ?? 0).format('DD MMMM YYYY');

  return (
    <S.Container>
      {payNowPrice && (
        <PlanCostItem
          costs={payNowPrice}
          onPopoverClicked={onPopoverClicked}
          infoTitle={tConfirmation('now.title')}
          infoDescription={tSummary('now.description')}
          popoverTitle={tSummary('now.popover-title')}
        />
      )}
      {payLaterPrice && (
        <PlanCostItem
          costs={payLaterPrice}
          onPopoverClicked={onPopoverClicked}
          infoTitle={tConfirmation('later.title')}
          infoDescription={tSummary('later.description')}
          popoverTitle={tSummary('later.popover-title')}
          billingDate={nextBillingDate}
        />
      )}
    </S.Container>
  );
};
